import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Stethoscope,
  GraduationCap,
  Microscope,
  Lightbulb,
  Users,
  Landmark,
  Briefcase,
  ScrollText,
  FileCheck2,
  LayoutGrid,
  School,
  Building2,
  ShieldCheck,
  Wrench,
} from "lucide-react";

import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../common_components/Toast";
import MyApplications from "../../faculty/page/MyApplications";
import MyNonFacultyApplications from "../../non-faculty/page/MyApplications";
import { ApplyPanel, NotifyPanel } from "../../../common_components/Panel";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { y: 16, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.4, ease: "easeOut" as const },
  },
};

const TABS = [
  { id: "overview", label: "Overview", Icon: LayoutGrid },
  { id: "recruitments", label: "Recruitments", Icon: Briefcase },
  { id: "notices", label: "Notices", Icon: ScrollText },
  { id: "applications", label: "My Applications", Icon: FileCheck2, authRequired: true },
];

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
}

const SectionHeader = ({ title, subtitle }: SectionHeaderProps) => (
  <div className="mb-6">
    <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
    {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
  </div>
);

interface OverviewPanelProps {
  isFaculty: boolean;
}

const OverviewPanel = ({ isFaculty }: OverviewPanelProps) => (
  <div className="space-y-10">
    <div>
      <SectionHeader
        title="Key Recruitment Areas"
        subtitle={
          isFaculty
            ? "We are inviting applications for various academic and clinical posts across multiple specialized departments."
            : "We offer administrative, technical, and support roles across various institute departments."
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(isFaculty
          ? [
              {
                Icon: Stethoscope,
                title: "Clinical Roles",
                desc: "Positions in Super-specialty and Broad-specialty departments such as Cardiology, Neurology, Pediatrics, and Surgery.",
              },
              {
                Icon: GraduationCap,
                title: "Academic & Teaching",
                desc: "Focus on undergraduate and postgraduate medical education, curriculum development, and mentorship.",
              },
              {
                Icon: Microscope,
                title: "Research & Administration",
                desc: "Opportunities in medical sciences, biostatistics and institutional administration.",
              },
            ]
          : [
              {
                Icon: Building2,
                title: "Administrative Roles",
                desc: "Positions in office administration, finance, accounts, store management, and human resources.",
              },
              {
                Icon: Wrench,
                title: "Technical & Engineering",
                desc: "Roles in civil engineering, electrical maintenance, IT infrastructure, and lab operations.",
              },
              {
                Icon: ShieldCheck,
                title: "Support & Nursing Cadre",
                desc: "Essential support positions in healthcare delivery, security, and hospital operational management.",
              },
            ]
        ).map(({ Icon, title, desc }) => (
          <div
            key={title}
            className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col gap-4 hover:border-slate-300 transition-colors"
          >
            <div className="w-9 h-9 bg-slate-100 text-slate-600 rounded-lg flex items-center justify-center">
              <Icon className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-1 leading-snug">{title}</h4>
              <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div>
      <SectionHeader
        title="Why AIIMS Mangalagiri is the Right Choice"
        subtitle="We offer a challenging and rewarding environment for growth, professional development, and service."
      />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          {
            Icon: Lightbulb,
            title: "Professional Growth",
            desc: "A culture that encourages continuous learning, operational leadership, and career advancement.",
          },
          {
            Icon: Users,
            title: "Collaborative Environment",
            desc: "Work alongside a dedicated team of professionals in a premier national medical institution.",
          },
          {
            Icon: Landmark,
            title: "State-of-the-Art Infrastructure",
            desc: "Modern campus facilities, technological integration, and comprehensive employee benefits.",
          },
        ].map(({ Icon, title, desc }) => (
          <div key={title} className="bg-slate-50 border border-slate-100 rounded-xl p-5">
            <div className="w-8 h-8 bg-white text-slate-500 border border-slate-200 rounded-lg flex items-center justify-center mb-4">
              <Icon className="w-4 h-4" />
            </div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">{title}</h4>
            <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

interface RecruitmentLandingPageProps {
  type: "FACULTY" | "NON_FACULTY";
}

export default function RecruitmentLandingPage({ type }: RecruitmentLandingPageProps) {
  const { apiRequest, isAuthenticated } = useAuth();
  const toast = useToast();

  const isFaculty = type === "FACULTY";
  const prefix = isFaculty ? "/faculty" : "/non-faculty";
  const noticeModule = isFaculty ? "Faculty Recruitment" : "Non-Faculty Recruitment";
  const portalTitle = isFaculty ? "Faculty Recruitment Portal" : "Non-Faculty Recruitment Portal";

  const [activeTab, setActiveTab] = useState("overview");
  const [allRecruitments, setAllRecruitments] = useState([]);
  const [notices, setNotices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [recruitmentPage, setRecruitmentPage] = useState(1);
  const [recruitmentTotalPages, setRecruitmentTotalPages] = useState(1);
  const [showArchivedRecruitments, setShowArchivedRecruitments] = useState(false);

  const [noticePage, setNoticePage] = useState(1);
  const [noticeTotalPages, setNoticeTotalPages] = useState(1);
  const [showArchivedNotices, setShowArchivedNotices] = useState(false);

  const fetchRecruitments = useCallback(async (page: number, showArchive: boolean) => {
    try {
      setIsLoading(true);
      const res = await apiRequest(`${prefix}/recruitment?page=${page}&limit=5&showArchive=${showArchive}`);
      setAllRecruitments(
        (res?.recruitments || []).map((rec: any) => ({
          id: rec.recruitmentId,
          title: rec.title,
          description: rec.description,
          endDate: rec.applicationDates.endDate,
          startDate: rec.applicationDates.startDate,
          status: String(rec.status).toLowerCase(),
          applicationLink: `${prefix}/${rec.recruitmentId}/applications`,
        }))
      );
      setRecruitmentTotalPages(res?.totalPages || 1);
    } catch (error: any) {
      console.error("Error fetching landing data:", error);
      toast.error(error.message || error.error || "Failed to load data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [apiRequest, prefix, toast]);

  const fetchNotices = useCallback(async (page: number, showArchive: boolean) => {
    try {
      setIsLoading(true);
      const res = await apiRequest(`/notifications?module=${encodeURIComponent(noticeModule)}&page=${page}&limit=5&showArchive=${showArchive}`);
      setNotices(res?.notices || []);
      setNoticeTotalPages(res?.totalPages || 1);
    } catch (error: any) {
      console.error("Error fetching notices:", error);
      toast.error(error.message || error.error || "Failed to load data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [apiRequest, noticeModule, toast]);

  useEffect(() => {
    if (activeTab === "recruitments") {
      fetchRecruitments(recruitmentPage, showArchivedRecruitments);
    } else if (activeTab === "notices") {
      fetchNotices(noticePage, showArchivedNotices);
    }
  }, [activeTab, fetchNotices, fetchRecruitments, noticePage, recruitmentPage, showArchivedNotices, showArchivedRecruitments]);

  useEffect(() => {
    if (!isAuthenticated && activeTab === "applications") {
      setActiveTab("overview");
    }
  }, [isAuthenticated, activeTab]);

  const visibleTabs = TABS.filter((t) => !t.authRequired || isAuthenticated);

  return (
    <div className="w-full bg-slate-50 min-h-[calc(100vh-64px)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-14 pb-20">
        <motion.section
          className="text-center mb-12"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex justify-center mb-5">
            <div className="w-14 h-14 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-sm">
              <School className="w-6 h-6 text-slate-700" />
            </div>
          </motion.div>

          <motion.div variants={itemVariants}>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 mb-2 tracking-tight leading-tight">
              {portalTitle}
            </h1>
            <p className="text-xs font-semibold text-slate-400 tracking-widest uppercase mb-5">
              All India Institute of Medical Sciences, Mangalagiri
            </p>
            <p className="text-sm text-slate-500 max-w-xl mx-auto leading-relaxed">
              Explore {isFaculty ? "academic and clinical" : "administrative and technical"} opportunities. Review open positions, monitor notices, and manage your applications.
            </p>
          </motion.div>
        </motion.section>

        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 mb-6 shadow-sm"
          role="tablist"
        >
          {visibleTabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={activeTab === id}
              onClick={() => {
                setActiveTab(id);
                setRecruitmentPage(1);
                setNoticePage(1);
              }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all ${
                activeTab === id
                  ? "bg-slate-900 text-white font-medium shadow-sm"
                  : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </motion.div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-8 shadow-sm min-h-[420px]">
          {activeTab === "overview" && <OverviewPanel isFaculty={isFaculty} />}

          {activeTab === "recruitments" && (
            <ApplyPanel
              admissions={allRecruitments}
              isLoading={isLoading}
              page={recruitmentPage}
              totalPages={recruitmentTotalPages}
              onPageChange={setRecruitmentPage}
              showArchive={showArchivedRecruitments}
              onShowArchiveChange={setShowArchivedRecruitments}
              title={`${isFaculty ? "Faculty" : "Non-Faculty"} Recruitments`}
              subtitle={`Open recruitment drives for ${isFaculty ? "faculty" : "non-faculty"} positions.`}
            />
          )}

          {activeTab === "notices" && (
            <NotifyPanel
              notices={notices}
              isLoading={isLoading}
              page={noticePage}
              totalPages={noticeTotalPages}
              onPageChange={setNoticePage}
              showArchive={showArchivedNotices}
              onShowArchiveChange={setShowArchivedNotices}
            />
          )}

          {activeTab === "applications" && isAuthenticated && (
            <div>
              <SectionHeader
                title="My Applications"
                subtitle="Track the status of your submitted applications."
              />
              {isFaculty ? <MyApplications /> : <MyNonFacultyApplications />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
